import { communicationService, eventsRepository, UapLoadStatus } from '@ad-engine/communication';
import {
	AdSlotClass,
	BaseServiceSetup,
	context,
	slotDataParamsUpdater,
	slotService,
	targetingService,
	utils,
} from '@ad-engine/core';
import { AD_ENGINE_UAP_LOAD_STATUS } from '../../communication/events/events-ad-engine-uap';
import { AnyclipBidsRefresher } from './anyclip-bids-refresher';
import { AnyclipTracker } from './anyclip-tracker';
import { isJwpApplicable } from '../jwp/is-applicable';

const logGroup = 'Anyclip';
const DEFAULT_WIDGET_NAME = '001w000001Y8ud2_19593';
const isPlayerAdSlotReady = (slotName = 'incontent_player') => {
	const adSlot = slotService.get(slotName);
	const domReady = !!document.getElementById(slotName);

	utils.logger(logGroup, `Waiting for player ad slot (${slotName}) ready`, domReady, adSlot);

	return domReady && adSlot !== null;
};

export class Anyclip extends BaseServiceSetup {
	private get pubname(): string {
		return context.get('services.anyclip.pubname') || 'fandomcom';
	}

	private get widgetname(): string {
		const widgetNameParam = context.get('services.anyclip.widgetname');

		if (['string', 'undefined'].includes(typeof widgetNameParam)) {
			return widgetNameParam || DEFAULT_WIDGET_NAME;
		}

		if (typeof widgetNameParam === 'object') {
			const wikiVertical = targetingService.get('s0v');
			return widgetNameParam[wikiVertical] || widgetNameParam['default'] || DEFAULT_WIDGET_NAME;
		}

		return DEFAULT_WIDGET_NAME;
	}

	private get dataVariant(): string {
		const dataVariantParam = context.get('experiments.floatingIcvWithDrawerExperiment.variant');

		if (dataVariantParam) {
			return dataVariantParam;
		}

		return '';
	}

	private get libraryUrl(): string {
		return (
			context.get('services.anyclip.libraryUrl') ||
			'//player.anyclip.com/anyclip-widget/lre-widget/prod/v1/src/lre.js'
		);
	}

	static isApplicable(): boolean {
		const isApplicableFunc: () => boolean = context.get('services.anyclip.isApplicable');

		return typeof isApplicableFunc === 'function' ? isApplicableFunc() : true;
	}

	private tracker: AnyclipTracker;
	private bidRefresher: AnyclipBidsRefresher;

	constructor() {
		super();
		this.tracker = new AnyclipTracker();
		this.bidRefresher = new AnyclipBidsRefresher();
	}

	call() {
		if (isJwpApplicable()) {
			return;
		}

		if (!this.isEnabled('services.anyclip.enabled', false)) {
			utils.logger(logGroup, 'disabled');
			return;
		}

		utils.logger(logGroup, 'initialized', this.pubname, this.widgetname, this.libraryUrl);
		communicationService.emit(eventsRepository.ANYCLIP_START);

		if (context.get('services.anyclip.loadWithoutAnchor')) {
			communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, (action: UapLoadStatus) => {
				if (!action.isLoaded) {
					this.loadPlayerAsset();
				}
			});

			return;
		}

		if (context.get('custom.hasIncontentPlayer')) {
			communicationService.on(AD_ENGINE_UAP_LOAD_STATUS, this.loadOnUapStatus.bind(this));
		}
	}

	reset() {
		utils.logger(logGroup, 'Destroying Anyclip widgets');
		window?.anyclip?.widgets?.forEach((w) => w?.destroy());
	}

	enableFloating() {
		utils.logger(logGroup, 'enabling Anyclip floating feature ');
		if (
			context.get('experiments.floatingIcvWithDrawerExperiment.variant') === 2 ||
			context.get('experiments.floatingIcvWithDrawerExperiment.variant') === 3
		) {
			document.getElementById('toc')?.classList.add('repositioned');
		}
		window?.anyclip?.getWidget()?.floatingModeToggle(true);
	}

	disableFloating() {
		utils.logger(logGroup, 'disabling Anyclip floating feature ');
		if (
			context.get('experiments.floatingIcvWithDrawerExperiment.variant') === 2 ||
			context.get('experiments.floatingIcvWithDrawerExperiment.variant') === 3
		) {
			document.getElementById('toc')?.classList.remove('repositioned');
		}
		window?.anyclip?.getWidget()?.floatingModeToggle(false);
	}

	get params(): Record<string, string> {
		return {
			pubname: this.pubname,
			widgetname: this.widgetname,
			'data-variant': this.dataVariant,
		};
	}

	private loadPlayerAsset(playerContainer: HTMLElement | null = null) {
		if (!Anyclip.isApplicable()) {
			utils.logger(logGroup, 'not applicable - aborting');
			return;
		}

		utils.logger(logGroup, 'loading Anyclip asset', this.libraryUrl);

		return utils.scriptLoader
			.loadScript(this.libraryUrl, true, playerContainer, this.params)
			.then(() => {
				utils.logger(logGroup, 'ready');

				this.waitForAnyclipReady().then(() => {
					communicationService.emit(eventsRepository.ANYCLIP_READY);
					const anyclipWidget = window?.anyclip?.getWidget();

					if (!anyclipWidget) {
						utils.logger(logGroup, 'No Anyclip widget found');
						return;
					}

					this.waitForPlayerAdSlot().then(() => {
						this.tracker.trackInit();
						this.bidRefresher.trySubscribingBidRefreshing(anyclipWidget);
					});

					this.tracker.registerAnyclipEvents(anyclipWidget);

					playerContainer?.classList.remove(AdSlotClass.HIDDEN_AD_CLASS);
				});
			});
	}

	private loadOnUapStatus({ isLoaded, adProduct }: UapLoadStatus) {
		this.tracker.trackEligible();

		if (!isLoaded && adProduct !== 'ruap') {
			if (!context.get('services.anyclip.latePageInject')) {
				utils.logger(logGroup, 'No need to wait for ANYCLIP_LATE_INJECT');
				this.initIncontentPlayer();
				return;
			}

			communicationService.on(eventsRepository.ANYCLIP_LATE_INJECT, () => {
				utils.logger(logGroup, 'ANYCLIP_LATE_INJECT emitted');
				this.initIncontentPlayer();
			});
		} else {
			utils.logger(logGroup, 'Anyclip blocked because of Fan Takeover');
		}
	}

	private initIncontentPlayer() {
		const slotName = 'incontent_player';
		const playerAdSlot = slotService.get(slotName);
		const playerElementId = context.get('services.anyclip.playerElementId');

		if (!playerAdSlot && !playerElementId) {
			utils.logger(logGroup, 'No incontent player - aborting');
			return;
		}

		if (playerElementId) {
			this.waitForPlayerAdSlot(playerElementId).then(() => {
				if (!isPlayerAdSlotReady()) {
					utils.logger(logGroup, 'No incontent player - aborting');
					return;
				}

				const playerElement = document.getElementById(playerElementId);
				if (playerElement) {
					this.loadPlayerAsset(playerElement);
					playerElement.dataset.slotLoaded = 'true';
				}
			});
			return;
		}

		slotDataParamsUpdater.updateOnCreate(playerAdSlot);
		this.loadPlayerAsset(playerAdSlot.getElement());
	}

	private waitForAnyclipReady(): Promise<void> {
		return new Promise((resolve) => {
			window.addEventListener('message', (event) => {
				if (event.data?.indexOf && event.data?.indexOf('lre:playerReady://') === 0) {
					resolve();
				}
			});
		});
	}

	private waitForPlayerAdSlot(playerAdSlotName = 'incontent_player'): Promise<boolean> {
		return new utils.WaitFor(() => isPlayerAdSlotReady(playerAdSlotName), 4, 250).until();
	}
}
